// apps/web/src/widgets/size-and-yield/format-yield.ts
//
// Phase 7 — size-and-yield yield-string substitution helper (D-02 substitution-
// only contract; NO expression mini-language). PURE helper — testable without
// any DOM. Substitutes ONLY {value} and {units} tokens; any other {token}
// returns undefined silently. The renderer (renderer.tsx) renders the
// "Yield: —" fallback when this helper returns undefined (UI-SPEC line 255
// success format + line 257 failure fallback + line 824 aria-live polite).
//
// PURITY: No observable side effects. The previous revision emitted
// `console.error` on non-finite multipliers, non-finite computed values, and
// unknown tokens. Per review finding WR-04, those calls violated the "pure
// helper" contract (file header), polluted user-facing developer consoles for
// expected-degenerate inputs, and made tests brittle (spy on console.error
// instead of assert on a return value). Observable signal is the renderer's
// responsibility (via the documented `undefined` return + the "Yield: —"
// fallback the renderer already implements); a future Phase 11 telemetry hook
// can intercept at the call site without this helper polluting global
// console state. If a richer error channel is needed later, prefer changing
// the return type to a discriminated union (e.g.,
// `{ kind: "ok" | "non-finite-input" | "unknown-token", ... }`) rather than
// re-introducing a console side effect.
//
// Locale-pinning: value.toLocaleString("en-US") for deterministic test output
// (the default JS locale varies by environment).
//
// Trailing-zero stripping: 4.0 → 4. Only the trailing ".0" (one or more zeros
// after a decimal point with NO non-zero digits) is stripped — 4.50 → 4.5 is
// out of scope (toLocaleString already produces "4.5" not "4.50").
import type { SizeAndYieldYieldDisplay } from "@the-recipe-spread/shared";

const ALLOWED_TOKEN_NAMES: ReadonlySet<string> = new Set<string>(["value", "units"]);
// Matches any {name} form where `name` is alphanumeric + underscore + hyphen.
// The capture group surfaces the token name so the helper can decide whether to
// substitute (allowed) or bail with undefined (unknown).
const ANY_TOKEN_RE = /\{([a-zA-Z0-9_-]+)\}/g;
const STRIP_TRAILING_ZERO_RE = /\.0+$/;

function stripTrailingZero(s: string): string {
  return s.replace(STRIP_TRAILING_ZERO_RE, "");
}

export function formatYield(
  yieldDisplay: SizeAndYieldYieldDisplay,
  multiplier: number,
): string | undefined {
  // Guard: non-finite multiplier (NaN / ±Infinity) — fall back so renderer
  // shows "Yield: —" instead of "NaN units" or "Infinity units".
  if (!Number.isFinite(multiplier)) {
    return undefined;
  }

  const value = yieldDisplay.baseValue * multiplier;

  // Defense-in-depth: baseValue × Infinity caught above, but a hostile
  // baseValue of Number.MAX_VALUE × 2 could overflow to Infinity.
  if (!Number.isFinite(value)) {
    return undefined;
  }

  // toLocaleString locale-pinned to en-US for deterministic test output.
  const valueStr = stripTrailingZero(value.toLocaleString("en-US"));

  // Walk every {token} in perStepFormula. Two-pass design avoids a partially
  // substituted string when an unknown token is later in the formula: if any
  // token is unknown, the helper returns undefined regardless of position.
  let unknownTokenFound = false;
  const result = yieldDisplay.perStepFormula.replace(
    ANY_TOKEN_RE,
    (match: string, tokenName: string): string => {
      if (!ALLOWED_TOKEN_NAMES.has(tokenName)) {
        unknownTokenFound = true;
        return match; // value discarded — we bail below
      }
      if (tokenName === "value") return valueStr;
      if (tokenName === "units") return yieldDisplay.unitsField;
      return match;
    },
  );

  if (unknownTokenFound) {
    return undefined;
  }

  return result;
}
